"use client";

import { useState } from "react";
import {
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  EnergyEquivalents,
  UnitEquivalents,
} from "@/components/tools/physics-mechanics/energy-tables";
import {
  RelationInputs,
  RelationResults,
} from "@/components/tools/physics-mechanics/relation-panel";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  formatSig,
  SIG_FIG_CHOICES,
  withUnit,
} from "@/lib/tool-kit/physics-mechanics/format";
import {
  evaluateRelation,
  initialRelationState,
} from "@/lib/tool-kit/physics-mechanics/relation";
import { POWER } from "@/lib/tool-kit/physics-mechanics/units";
import {
  EFFICIENCY_ENERGY,
  EFFICIENCY_POWER,
  type EfficiencyKey,
  POWER_FORCE,
  POWER_WORK,
  type PowerForceKey,
  type PowerWorkKey,
  WORK,
  type WorkKey,
} from "@/lib/tool-kit/physics-mechanics/work";

/** English strings. A translated content file overrides these through `ui`. */
export const WORK_AND_POWER_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modeWork: "Work (W = Fd cos θ)",
  modePowerWork: "Power from work and time (P = W ÷ t)",
  modePowerForce: "Power from force and speed (P = Fv)",
  modeEfficiency: "Efficiency",
  workHint:
    "Work is force times the distance moved in the direction of the force. θ is the angle between the force and the motion: 0° pushes along the motion (positive work), 90° does no work, 180° opposes the motion (negative work).",
  powerWorkHint:
    "Power is the rate of doing work: one watt is one joule per second.",
  powerForceHint:
    "A force acting on something moving at speed v delivers power Fv cos θ. For a car at steady speed, F is the force the wheels push the road with.",
  efficiencyHint:
    "Efficiency is the useful output divided by the total input. It can never exceed 100%: the difference is lost, mostly as heat.",
  efficiencyKind: "Compare",
  efficiencyEnergy: "energy (joules)",
  efficiencyPower: "power (watts)",
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  calculated: "Calculated",
  rounding: "Round answers to",
  sigFigs: "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  work: "Work W",
  force: "Force F",
  distance: "Distance moved d",
  angle: "Angle θ between force and motion",
  power: "Power P",
  time: "Time t",
  speed: "Speed v",
  efficiency: "Efficiency η",
  usefulOut: "Useful output",
  totalIn: "Total input",
  angleHint: "Between 0° and 180°.",
  workingHeading: "The working",
  stepEquation: "Start from {equation}.",
  stepRearrange: "Rearrange for {symbol}: {form}.",
  stepConvert: "Convert to SI units: {list}.",
  stepSubstitute: "{symbol} = {working} = {value}",
  stepAnswerUnit: "In the unit you chose: {si} = {shown}.",
  copy: "Copy result",
  copied: "Copied!",
  enterAll: "Enter every value except the one you are solving for.",
  unreadable:
    "{name} is not a number. Use digits, a decimal point and an optional minus sign.",
  errors: {
    "force-negative":
      "Give the force as a positive size; the angle sets whether it helps or opposes the motion.",
    "distance-negative":
      "Give the distance as a positive number; use an angle above 90° for a force that opposes the motion.",
    "speed-negative": "Give the speed as a positive number.",
    "time-negative": "Time cannot be negative.",
    "force-zero":
      "The force is zero, so this value cannot be found from the work. Enter a force above zero.",
    "distance-zero":
      "The distance is zero, so the force cannot be found from the work. Enter a distance above zero.",
    "speed-zero":
      "The speed is zero, so the force cannot be found from the power. Enter a speed above zero.",
    "force-distance-zero":
      "The angle needs a force and a distance that are both above zero.",
    "force-speed-zero":
      "The angle needs a force and a speed that are both above zero.",
    "angle-right":
      "At 90° the force does no work and delivers no power, so the force, distance or speed cannot be found. Choose a different angle.",
    "ratio-out-of-range":
      "No angle fits: the work (or power) is larger than force × distance (or force × speed), which is the most any angle can give.",
    "sign-mismatch":
      "That combination needs a negative force or distance. Use an angle above 90° if the force opposes the motion.",
    "time-zero":
      "Time is zero, so the power cannot be found. Enter a time above zero.",
    "power-zero":
      "Power is zero, so the time cannot be found. Enter a power above zero.",
    "input-negative": "The input cannot be negative.",
    "output-negative": "The output cannot be negative.",
    "input-zero": "The input must be greater than zero to find an efficiency.",
    "over-unity":
      "Efficiency cannot exceed 100%: a machine cannot give out more energy than it takes in.",
    "efficiency-negative": "Efficiency cannot be negative.",
    "efficiency-zero":
      "At 0% efficiency nothing useful comes out, so the input cannot be found from the output.",
    "not-finite":
      "The result is too large to calculate. Check the numbers and units.",
  },
  equivalentHeading: "The same value in other units",
  columnUnit: "Unit",
  columnValue: "Value",
  wasted: "Lost (input − output)",
  lostShare: "Share lost",
  assumptions:
    "Assumes a constant force along a straight path. For a force that varies, work is the area under its force–distance graph.",
} as const;

type Mode = "work" | "powerWork" | "powerForce" | "efficiency";
type EfficiencyKind = "energy" | "power";

const ENERGY_UNITS = ["J", "kJ", "kcal", "Wh", "kWh", "ftlbf"] as const;
const POWER_UNITS = ["W", "kW", "hp", "PS", "hpE"] as const;

const INITIAL_WORK = initialRelationState<WorkKey>(WORK, "W", {
  F: { value: "50", unit: "N" },
  d: { value: "10", unit: "m" },
  a: { value: "30", unit: "deg" },
});
const INITIAL_POWER_WORK = initialRelationState<PowerWorkKey>(POWER_WORK, "P", {
  W: { value: "36", unit: "kJ" },
  t: { value: "30", unit: "s" },
});
const INITIAL_POWER_FORCE = initialRelationState<PowerForceKey>(
  POWER_FORCE,
  "P",
  {
    F: { value: "2000", unit: "N" },
    v: { value: "15", unit: "mps" },
    a: { value: "0", unit: "deg" },
  }
);
const INITIAL_EFFICIENCY_ENERGY = initialRelationState<EfficiencyKey>(
  EFFICIENCY_ENERGY,
  "eta",
  {
    out: { value: "600", unit: "J" },
    in: { value: "800", unit: "J" },
  }
);
const INITIAL_EFFICIENCY_POWER = initialRelationState<EfficiencyKey>(
  EFFICIENCY_POWER,
  "eta",
  {
    out: { value: "600", unit: "W" },
    in: { value: "800", unit: "W" },
  }
);

export default function WorkAndPowerCalculatorWidget() {
  const strings = useToolUi(WORK_AND_POWER_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [mode, setMode] = useState<Mode>("work");
  const [work, setWork] = useState(INITIAL_WORK);
  const [powerWork, setPowerWork] = useState(INITIAL_POWER_WORK);
  const [powerForce, setPowerForce] = useState(INITIAL_POWER_FORCE);
  const [kind, setKind] = useState<EfficiencyKind>("energy");
  const [effEnergy, setEffEnergy] = useState(INITIAL_EFFICIENCY_ENERGY);
  const [effPower, setEffPower] = useState(INITIAL_EFFICIENCY_POWER);
  const [sigFigs, setSigFigs] = useState("4");
  const sig = Number(sigFigs);
  const panel = {
    ...strings,
    errors: strings.errors as Record<string, string>,
  };
  const common = { fmt, sig, strings: panel };
  const workLabels: Record<WorkKey, string> = {
    W: strings.work,
    F: strings.force,
    d: strings.distance,
    a: strings.angle,
  };
  const powerWorkLabels: Record<PowerWorkKey, string> = {
    P: strings.power,
    W: strings.work,
    t: strings.time,
  };
  const powerForceLabels: Record<PowerForceKey, string> = {
    P: strings.power,
    F: strings.force,
    v: strings.speed,
    a: strings.angle,
  };
  const effLabels: Record<EfficiencyKey, string> = {
    eta: strings.efficiency,
    out: strings.usefulOut,
    in: strings.totalIn,
  };
  const workEval = evaluateRelation(WORK, work);
  const powerWorkEval = evaluateRelation(POWER_WORK, powerWork);
  const powerForceEval = evaluateRelation(POWER_FORCE, powerForce);
  const effEnergyEval = evaluateRelation(EFFICIENCY_ENERGY, effEnergy);
  const effPowerEval = evaluateRelation(EFFICIENCY_POWER, effPower);

  const table = (family: "energy" | "power", value: number) =>
    family === "energy" ? (
      <EnergyEquivalents
        columnUnit={strings.columnUnit}
        columnValue={strings.columnValue}
        fmt={fmt}
        heading={strings.equivalentHeading}
        joules={value}
        sig={sig}
        unitIds={ENERGY_UNITS}
      />
    ) : (
      <UnitEquivalents
        columnUnit={strings.columnUnit}
        columnValue={strings.columnValue}
        family={POWER}
        fmt={fmt}
        heading={strings.equivalentHeading}
        sig={sig}
        unitIds={POWER_UNITS}
        value={value}
      />
    );
  const lossCards = (all: Record<EfficiencyKey, number>, unit: string) => {
    const lost = all.in - all.out;
    const share = all.in > 0 ? lost / all.in : 0;
    return (
      <StatGrid>
        <StatCard
          label={strings.wasted}
          value={withUnit(formatSig(fmt, lost, sig), unit)}
        />
        <StatCard
          label={strings.lostShare}
          value={withUnit(formatSig(fmt, share * 100, sig), "%")}
        />
      </StatGrid>
    );
  };

  const hints = {
    work: strings.workHint,
    powerWork: strings.powerWorkHint,
    powerForce: strings.powerForceHint,
    efficiency: strings.efficiencyHint,
  };
  const effDef = kind === "energy" ? EFFICIENCY_ENERGY : EFFICIENCY_POWER;
  const effState = kind === "energy" ? effEnergy : effPower;
  const effEval = kind === "energy" ? effEnergyEval : effPowerEval;
  const setEff = kind === "energy" ? setEffEnergy : setEffPower;

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="wp-mode"
        onChange={setMode}
        options={[
          { value: "work", label: strings.modeWork },
          { value: "powerWork", label: strings.modePowerWork },
          { value: "powerForce", label: strings.modePowerForce },
          { value: "efficiency", label: strings.modeEfficiency },
        ]}
        value={mode}
      />
      <p className={HINT}>{hints[mode]}</p>
      {mode === "work" ? (
        <RelationInputs
          {...common}
          def={WORK}
          evaluation={workEval}
          hints={{ a: strings.angleHint }}
          idPrefix="wp-work"
          labels={workLabels}
          onState={setWork}
          state={work}
        />
      ) : null}
      {mode === "powerWork" ? (
        <RelationInputs
          {...common}
          def={POWER_WORK}
          evaluation={powerWorkEval}
          idPrefix="wp-pw"
          labels={powerWorkLabels}
          onState={setPowerWork}
          state={powerWork}
        />
      ) : null}
      {mode === "powerForce" ? (
        <RelationInputs
          {...common}
          def={POWER_FORCE}
          evaluation={powerForceEval}
          hints={{ a: strings.angleHint }}
          idPrefix="wp-pf"
          labels={powerForceLabels}
          onState={setPowerForce}
          state={powerForce}
        />
      ) : null}
      {mode === "efficiency" ? (
        <>
          <PillGroup
            label={strings.efficiencyKind}
            name="wp-eff-kind"
            onChange={setKind}
            options={[
              { value: "energy", label: strings.efficiencyEnergy },
              { value: "power", label: strings.efficiencyPower },
            ]}
            value={kind}
          />
          <RelationInputs
            {...common}
            def={effDef}
            evaluation={effEval}
            idPrefix={`wp-eff-${kind}`}
            labels={effLabels}
            onState={setEff}
            state={effState}
          />
        </>
      ) : null}
      <div className="w-60">
        <SelectField
          id="wp-sig"
          label={strings.rounding}
          onChange={setSigFigs}
          options={SIG_FIG_CHOICES.map((choice) => ({
            value: choice,
            label: tPlural(strings.sigFigs, locale, Number(choice), {
              count: fmt(Number(choice)),
            }),
          }))}
          value={sigFigs}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "work" ? (
          <RelationResults
            {...common}
            def={WORK}
            evaluation={workEval}
            extras={(all) => (
              <>
                {table("energy", all.W)}
                <p className={HINT}>{strings.assumptions}</p>
              </>
            )}
            labels={workLabels}
            state={work}
          />
        ) : null}
        {mode === "powerWork" ? (
          <RelationResults
            {...common}
            def={POWER_WORK}
            evaluation={powerWorkEval}
            extras={(all) => table("power", all.P)}
            labels={powerWorkLabels}
            state={powerWork}
          />
        ) : null}
        {mode === "powerForce" ? (
          <RelationResults
            {...common}
            def={POWER_FORCE}
            evaluation={powerForceEval}
            extras={(all) => table("power", all.P)}
            labels={powerForceLabels}
            state={powerForce}
          />
        ) : null}
        {mode === "efficiency" ? (
          <RelationResults
            {...common}
            def={effDef}
            evaluation={effEval}
            extras={(all) => lossCards(all, kind === "energy" ? "J" : "W")}
            labels={effLabels}
            state={effState}
          />
        ) : null}
      </section>
    </>
  );
}
